import { useState, type FormEvent } from 'react';
import { Link } from '@tanstack/react-router';

import { Button } from '@/components/ui/button';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Sheet,
  SheetBody,
  SheetContent,
  SheetDescription,
  SheetHeader,
  SheetTitle,
} from '@/components/ui/sheet';
import { CapacitySlots } from '@/features/concurrency';
import { formatMoney } from '@/features/workspace-usage';
import { PeriodType } from '@/lib/api/generated/model';
import type { WorkspaceLimitRow } from '@/lib/api/generated/model';

import { parseConcurrencyLimit } from '../concurrencyLimit';
import {
  useClearConcurrencyLimit,
  useSetConcurrencyLimit,
} from '../hooks/useConcurrency';
import {
  useClearWorkspaceLimit,
  useSetWorkspaceLimit,
} from '../hooks/useLimits';
import { parseLimitAmount } from '../limitAmount';
import {
  useClearMemoryLimit,
  useSetMemoryLimit,
} from '../hooks/useMemoryLimit';
import {
  countIn,
  formatMemory,
  parseMemoryLimit,
  unitFor,
  type MemoryUnit,
} from '../memoryLimit';
import { overrideRemovalConsequence } from '../overrideConsequence';
import {
  capSpendText,
  limitSpendOf,
  type LimitsPolicy,
  type WorkspaceLimitsRow,
  type WorkspaceMemory,
  type WorkspaceRuns,
} from '../workspaceLimits';
import { AddWorkflowLimitDialog } from './AddWorkflowLimitDialog';
import { LimitField } from './LimitField';
import { PeriodField } from './PeriodField';
import { RemoveWorkflowLimitDialog } from './RemoveWorkflowLimitDialog';
import { WorkflowLimitDialog } from './WorkflowLimitDialog';

export interface WorkspaceLimitsSheetProps {
  /** The workspace whose sheet is open; `undefined` closes it. */
  row: WorkspaceLimitsRow | undefined;
  policy: LimitsPolicy;
  currency: string;
  hasRuns: boolean;
  /** Whether the memory read is available at all; an older service has none. */
  hasMemory: boolean;
  /** The spend read's rows, which the cap dialogs judge a cap against. */
  usageRows: readonly WorkspaceLimitRow[];
  onClose: () => void;
}

const SECTION = 'border-border flex flex-col gap-3 border-t px-4 py-4';
const SECTION_TITLE =
  'text-muted-foreground text-xs font-medium tracking-wide uppercase';

/**
 * Every limit of one workspace, changed in one place (spec 117 US3): how many runs it
 * may have at once, what it may spend, and its workflow caps. Each limit says whether it
 * follows the organization's default or is custom, and turning "Custom limit" off is how
 * a custom limit is removed.
 *
 * Each section keeps its own draft and its own error: a failed save leaves the sheet
 * open with the reason beside the control that failed, and nothing else is lost.
 */
export function WorkspaceLimitsSheet({
  row,
  policy,
  currency,
  hasRuns,
  hasMemory,
  usageRows,
  onClose,
}: WorkspaceLimitsSheetProps) {
  return (
    <Sheet
      open={row !== undefined}
      onOpenChange={(open) => {
        if (!open) onClose();
      }}
    >
      {row ? (
        <SheetContent
          // Back to the row that opened it (FR-019). The row outlives the sheet: it is
          // found by its workspace, not held as a ref across refetches.
          finalFocus={() =>
            document.querySelector<HTMLElement>(
              `tr[data-workspace-id="${row.workspaceId}"]`,
            )
          }
        >
          <SheetHeader>
            <SheetTitle>{row.name}</SheetTitle>
            <SheetDescription>Every limit on this workspace</SheetDescription>
            {/* Spec 073 FR-019, kept: the console links into a workspace's usage page
                and nowhere else inside it. */}
            <Link
              to="/workspaces/$workspaceId/ai-usage/spend"
              params={{ workspaceId: row.workspaceId }}
              className="text-primary text-sm underline-offset-2 hover:underline"
            >
              Open usage
            </Link>
          </SheetHeader>
          <SheetBody className="flex flex-col p-0">
            {hasRuns && row.runs && policy.ceiling !== null ? (
              <RunsSection
                // Re-read from the service after every save: a remount on a new
                // source drops a draft that no longer describes the workspace.
                key={`${row.workspaceId}-${row.runs.source}-${String(row.runs.limit)}`}
                workspaceId={row.workspaceId}
                runs={row.runs}
                policy={policy}
                ceiling={policy.ceiling}
              />
            ) : null}
            {hasMemory && row.memory ? (
              <MemorySection
                // The unit is part of the saved state, so it is part of the key too.
                key={`${row.workspaceId}-${row.memory.source}-${String(row.memory.amountMib)}-${row.memory.unit}`}
                workspaceId={row.workspaceId}
                memory={row.memory}
                policy={policy}
                minimum={policy.memoryMinimum ?? 1}
              />
            ) : null}
            <SpendSection
              key={`${row.workspaceId}-${String(row.spendCustom)}-${row.spendLimit ?? ''}`}
              row={row}
              policy={policy}
              currency={currency}
            />
            <CapsSection row={row} currency={currency} usageRows={usageRows} />
          </SheetBody>
        </SheetContent>
      ) : null}
    </Sheet>
  );
}

/** An on/off control that says what it turns on. */
function CustomSwitch({
  checked,
  onChange,
  disabled = false,
}: {
  checked: boolean;
  onChange: (next: boolean) => void;
  disabled?: boolean;
}) {
  return (
    <button
      type="button"
      role="switch"
      aria-checked={checked}
      disabled={disabled}
      onClick={() => {
        onChange(!checked);
      }}
      className="focus-visible:ring-ring touch-target inline-flex items-center gap-2 rounded-md text-sm focus-visible:ring-3 focus-visible:outline-none disabled:opacity-50"
    >
      <span
        aria-hidden="true"
        className={`relative h-[18px] w-8 rounded-full transition-colors ${checked ? 'bg-primary' : 'bg-border'}`}
      >
        <span
          className={`bg-background absolute top-0.5 size-3.5 rounded-full shadow-raised transition-[left] ${checked ? 'left-4' : 'left-0.5'}`}
        />
      </span>
      Custom limit
    </button>
  );
}

function RunsSection({
  workspaceId,
  runs,
  policy,
  ceiling,
}: {
  workspaceId: string;
  runs: WorkspaceRuns;
  policy: LimitsPolicy;
  ceiling: number;
}) {
  const saved = runs.source === 'workspace';
  const [custom, setCustom] = useState(saved);
  const [draft, setDraft] = useState(String(runs.limit));
  const [refusal, setRefusal] = useState<string | null>(null);
  const set = useSetConcurrencyLimit(ceiling);
  const clear = useClearConcurrencyLimit(ceiling);
  const busy = set.isPending || clear.isPending;
  // A saved custom limit has nothing to save until its number changes; one just
  // switched on is a change in itself — saving pins the workspace to that number.
  const unchanged = saved && Number(draft) === runs.limit;

  const step = (by: number) => {
    const current = Number.parseInt(draft, 10);
    const next = Number.isNaN(current) ? runs.limit : current + by;
    setDraft(String(Math.min(ceiling, Math.max(1, next))));
    setRefusal(null);
  };

  const save = (event: FormEvent) => {
    event.preventDefault();
    const parsed = parseConcurrencyLimit(draft, ceiling);
    if (!parsed.ok) {
      setRefusal(parsed.reason);
      return;
    }
    setRefusal(null);
    set.mutate({ workspaceId, value: parsed.value });
  };

  const follows =
    policy.runsDefault === null
      ? `Follows the installation default (${String(policy.fallback)})`
      : `Follows the organization default (${String(policy.runsDefault)})`;
  const error = refusal ?? (set.error?.message || clear.error?.message || null);

  return (
    <section className={SECTION}>
      <h3 className={SECTION_TITLE}>Runs at once</h3>
      <div className="flex items-center justify-between gap-3">
        <CapacitySlots
          running={runs.running}
          limit={runs.limit}
          queued={runs.queued}
          size="md"
        />
        <span className="text-muted-foreground text-sm tabular-nums">
          {`${String(runs.running)} running`}
          {runs.queued > 0 ? ` · ${String(runs.queued)} waiting` : ''}
        </span>
      </div>
      <CustomSwitch
        checked={custom}
        disabled={busy}
        onChange={(next) => {
          setRefusal(null);
          if (!next && saved) {
            clear.mutate({ workspaceId });
            return;
          }
          setCustom(next);
          setDraft(String(runs.limit));
        }}
      />
      {custom ? (
        // `noValidate`: the browser's own range check would block the submit in
        // silence; the refusal belongs beside the field, in words (FR-010).
        <form
          noValidate
          onSubmit={save}
          className="flex flex-wrap items-center gap-2"
        >
          <span className="border-border inline-flex items-center overflow-hidden rounded-md border">
            <button
              type="button"
              aria-label="One fewer"
              onClick={() => {
                step(-1);
              }}
              className="bg-muted hover:bg-accent size-8"
            >
              −
            </button>
            <input
              type="number"
              aria-label="Runs at once"
              min={1}
              max={ceiling}
              value={draft}
              onChange={(event) => {
                setDraft(event.target.value);
                setRefusal(null);
              }}
              aria-invalid={error !== null}
              className="w-12 [appearance:textfield] bg-transparent text-center font-bold tabular-nums outline-none [&::-webkit-inner-spin-button]:appearance-none"
            />
            <button
              type="button"
              aria-label="One more"
              onClick={() => {
                step(1);
              }}
              className="bg-muted hover:bg-accent size-8"
            >
              +
            </button>
          </span>
          <Button type="submit" size="sm" disabled={busy || unchanged}>
            {set.isPending ? 'Saving…' : 'Save'}
          </Button>
          <span className="text-muted-foreground text-xs">
            {`Allowed 1–${String(ceiling)}`}
          </span>
        </form>
      ) : (
        <p className="text-muted-foreground text-sm">{follows}</p>
      )}
      {runs.clamped ? (
        <p className="text-muted-foreground text-xs">
          {`Clamped to ${String(ceiling)}, the installation ceiling`}
        </p>
      ) : null}
      {error ? (
        <p role="alert" className="text-destructive text-sm">
          {error}
        </p>
      ) : null}
    </section>
  );
}

const UNITS: readonly MemoryUnit[] = ['MiB', 'GiB'];

function MemorySection({
  workspaceId,
  memory,
  policy,
  minimum,
}: {
  workspaceId: string;
  memory: WorkspaceMemory;
  policy: LimitsPolicy;
  /** The least amount accepted; there is no maximum. */
  minimum: number;
}) {
  const saved = memory.source === 'workspace';
  const [custom, setCustom] = useState(saved);
  const [draft, setDraft] = useState(
    String(countIn(memory.amountMib, memory.unit)),
  );
  const [unit, setUnit] = useState<MemoryUnit>(memory.unit);
  const [refusal, setRefusal] = useState<string | null>(null);
  const set = useSetMemoryLimit();
  const clear = useClearMemoryLimit();
  const busy = set.isPending || clear.isPending;
  // The amount and its unit are one saved state, so changing only the unit is a change.
  const parsed = parseMemoryLimit(draft, unit, minimum);
  const unchanged =
    saved &&
    unit === memory.unit &&
    parsed.ok &&
    parsed.value_mib === memory.amountMib;

  const edited = () => {
    setRefusal(null);
    set.reset();
  };

  const save = (event: FormEvent) => {
    event.preventDefault();
    if (!parsed.ok) {
      setRefusal(parsed.reason);
      return;
    }
    setRefusal(null);
    set.mutate({ workspaceId, value_mib: parsed.value_mib, unit: parsed.unit });
  };

  const follows =
    policy.memoryDefault === null
      ? `Follows the installation default (${formatMemory(policy.memoryFallback ?? memory.amountMib, 'MiB')})`
      : `Follows the organization default (${formatMemory(policy.memoryDefault, policy.memoryDefaultUnit ?? 'MiB')})`;
  const error = refusal ?? (set.error?.message || clear.error?.message || null);

  return (
    <section className={SECTION}>
      <h3 className={SECTION_TITLE}>Memory per run</h3>
      <p className="text-muted-foreground text-xs">
        {`Applies to runs that start after it is saved, to every step of every workflow in this workspace. A different amount for one step is not supported yet.`}
      </p>
      <CustomSwitch
        checked={custom}
        disabled={busy}
        onChange={(next) => {
          edited();
          if (!next && saved) {
            clear.mutate({ workspaceId });
            return;
          }
          setCustom(next);
          // Switching on starts from what is enforced now, in the unit it is reported in.
          setDraft(String(countIn(memory.amountMib, memory.unit)));
          setUnit(memory.unit);
        }}
      />
      {custom ? (
        // `noValidate`: a refusal belongs beside the field, in words, not as a
        // browser tooltip that blocks the submit in silence.
        <form
          noValidate
          onSubmit={save}
          className="flex flex-wrap items-center gap-2"
        >
          <input
            type="number"
            aria-label="Memory per run"
            value={draft}
            onChange={(event) => {
              setDraft(event.target.value);
              edited();
            }}
            aria-invalid={error !== null}
            className="border-input h-9 w-24 [appearance:textfield] rounded-md border bg-transparent px-3 text-sm tabular-nums outline-none [&::-webkit-inner-spin-button]:appearance-none"
          />
          <Select
            value={unit}
            onValueChange={(next) => {
              if (!next) return;
              setUnit(next as MemoryUnit);
              edited();
            }}
            disabled={busy}
          >
            <SelectTrigger aria-label="Unit" className="w-24">
              <SelectValue>{(value: string) => value}</SelectValue>
            </SelectTrigger>
            <SelectContent>
              {UNITS.map((option) => (
                <SelectItem key={option} value={option}>
                  {option}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          <Button type="submit" size="sm" disabled={busy || unchanged}>
            {set.isPending ? 'Saving…' : 'Save'}
          </Button>
          <span className="text-muted-foreground w-full text-xs">
            {`Allowed from ${formatMemory(minimum, unitFor(minimum))}`}
          </span>
        </form>
      ) : (
        <p className="text-muted-foreground text-sm">{follows}</p>
      )}
      {error ? (
        <p role="alert" className="text-destructive text-sm">
          {error}
        </p>
      ) : null}
    </section>
  );
}

function SpendSection({
  row,
  policy,
  currency,
}: {
  row: WorkspaceLimitsRow;
  policy: LimitsPolicy;
  currency: string;
}) {
  const [custom, setCustom] = useState(row.spendCustom);
  const [confirming, setConfirming] = useState(false);
  const [amount, setAmount] = useState(
    row.spendCustom ? (row.spendLimit ?? '') : '',
  );
  const [period, setPeriod] = useState<PeriodType>(
    row.spendPeriod ?? PeriodType.monthly,
  );
  const [submitted, setSubmitted] = useState(false);
  const set = useSetWorkspaceLimit();
  const clear = useClearWorkspaceLimit();
  const parsed = parseLimitAmount(amount);
  const amountError = submitted && !parsed.ok ? parsed.reason : null;
  const savedPeriod = row.spendPeriod ?? PeriodType.monthly;
  // Nothing to save until there is an amount and it, or the period, differs from the
  // saved custom limit. Compared as numbers: "21" and "21.00" are the same limit.
  const unchanged =
    amount.trim() === '' ||
    (row.spendCustom &&
      row.spendLimit !== null &&
      Number(amount) === Number(row.spendLimit) &&
      period === savedPeriod);
  const share =
    row.spendLimit === null || Number(row.spendLimit) <= 0
      ? 0
      : Math.min(1, Number(limitSpendOf(row)) / Number(row.spendLimit));

  return (
    <section className={SECTION}>
      <h3 className={SECTION_TITLE}>Spend</h3>
      <div className="flex flex-col gap-1">
        <span
          aria-hidden="true"
          className="bg-border h-1.5 overflow-hidden rounded-full"
        >
          <span
            className="bg-primary block h-full rounded-full"
            style={{ width: `${String(share * 100)}%` }}
          />
        </span>
        <span className="text-muted-foreground text-xs tabular-nums">
          {row.spendLimit === null
            ? `${formatMoney(row.spend, currency)} spent this period · no limit`
            : row.limitOwn
              ? `${formatMoney(row.limitOwn.spend, currency)} ${row.limitOwn.phrase} of ${formatMoney(row.spendLimit, currency)} · ${formatMoney(row.spend, currency)} this period`
              : `${formatMoney(row.spend, currency)} of ${formatMoney(row.spendLimit, currency)} this period`}
        </span>
      </div>
      <CustomSwitch
        checked={custom}
        disabled={set.isPending || clear.isPending}
        onChange={(next) => {
          if (!next && row.spendCustom) {
            // Removing a custom spend limit can stop a workspace, so it is stated
            // before it is done (the removal consequence rule, spec 074).
            setCustom(false);
            setConfirming(true);
            return;
          }
          setCustom(next);
          setConfirming(false);
        }}
      />
      {confirming ? (
        <div className="border-border flex flex-col gap-2 rounded-md border border-dashed px-3 py-2">
          <p role="status" className="text-sm">
            {overrideRemovalConsequence(
              row.usageRow ?? { spend: row.spend },
              policy.spendDefault,
              currency,
              row.readPeriod,
            )}
          </p>
          <div className="flex gap-2">
            <Button
              size="sm"
              variant="destructive"
              disabled={clear.isPending}
              onClick={() => {
                clear.mutate(row.workspaceId);
              }}
            >
              {clear.isPending ? 'Removing…' : 'Remove custom limit'}
            </Button>
            <Button
              size="sm"
              variant="outline"
              disabled={clear.isPending}
              onClick={() => {
                setConfirming(false);
                setCustom(true);
              }}
            >
              Keep it
            </Button>
          </div>
        </div>
      ) : custom ? (
        <form
          className="flex flex-col gap-3"
          onSubmit={(event) => {
            event.preventDefault();
            setSubmitted(true);
            if (!parsed.ok) return;
            set.mutate({
              workspaceId: row.workspaceId,
              amount: parsed.amount,
              periodType: period,
            });
          }}
        >
          <LimitField
            label="Amount per period"
            value={amount}
            onChange={(next) => {
              setAmount(next);
              setSubmitted(false);
            }}
            currency={currency}
            error={amountError}
          />
          <PeriodField value={period} onChange={setPeriod} />
          <Button
            type="submit"
            size="sm"
            className="self-start"
            disabled={set.isPending || unchanged}
          >
            {set.isPending ? 'Saving…' : 'Save'}
          </Button>
        </form>
      ) : (
        <p className="text-muted-foreground text-sm">
          {policy.spendDefault === null
            ? 'Follows the organization default — no limit'
            : `Follows the organization default (${formatMoney(policy.spendDefault, currency)})`}
        </p>
      )}
      {set.isError || clear.isError ? (
        <p role="alert" className="text-destructive text-sm">
          {(set.error ?? clear.error)?.message}
        </p>
      ) : null}
    </section>
  );
}

function CapsSection({
  row,
  currency,
  usageRows,
}: {
  row: WorkspaceLimitsRow;
  currency: string;
  usageRows: readonly WorkspaceLimitRow[];
}) {
  return (
    <section className={SECTION}>
      <h3 className={SECTION_TITLE}>Workflow caps</h3>
      {row.caps.length === 0 ? (
        <p className="text-muted-foreground text-sm">
          No workflow in this workspace has a cap of its own.
        </p>
      ) : (
        <ul className="flex flex-col gap-2">
          {row.caps.map((cap) => (
            <li
              key={cap.workflow_id}
              className="flex flex-wrap items-center justify-between gap-2"
            >
              <span className="flex min-w-0 flex-col">
                <span className="truncate text-sm font-medium">
                  {cap.workflow_name}
                </span>
                <span className="text-muted-foreground text-xs tabular-nums">
                  {capSpendText(cap, row.readPeriod, cap.currency ?? currency)}
                </span>
              </span>
              <span className="flex gap-1">
                <WorkflowLimitDialog
                  row={cap}
                  workspaces={usageRows}
                  currency={cap.currency ?? currency}
                  readPeriod={row.readPeriod}
                />
                <RemoveWorkflowLimitDialog row={cap} />
              </span>
            </li>
          ))}
        </ul>
      )}
      <div>
        <AddWorkflowLimitDialog
          workspaces={usageRows}
          cappedWorkflowIds={new Set(row.caps.map((cap) => cap.workflow_id))}
          currency={currency}
          fixedWorkspaceId={row.workspaceId}
        />
      </div>
    </section>
  );
}
